// frontend/src/components/admin/DeviceCard.jsx

import React, { useState, useEffect } from 'react';
import api from '../../services/api';

const DeviceCard = ({ device, onClick }) => {
  const [controlling, setControlling] = useState(false);
  const [deviceState, setDeviceState] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (device.type === 'switch') {
      fetchDeviceState();
    }
  }, [device]);

  const fetchDeviceState = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/devices/${device.deviceId}`);
      const status = response.data.data.realtimeStatus;
      const switchData = status?.find(s => s.code === 'switch' || s.code === 'switch_1');
      setDeviceState(switchData?.value === true);
    } catch (error) {
      console.error('Erreur récupération état:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDirectControl = async (action, e) => {
    e.stopPropagation(); // Empêcher l'ouverture des détails
    
    if (controlling) return;

    setControlling(true);
    try {
      await api.post(`/devices/${device.deviceId}/direct-control`, { action });
      await fetchDeviceState(); // Rafraîchir l'état
      alert(`Device ${action === 'on' ? 'allumé' : 'éteint'} avec succès`);
    } catch (error) {
      const errorMsg = error.response?.data?.message || error.message || 'Erreur lors du contrôle';
      alert(`❌ ${errorMsg}`);
    } finally {
      setControlling(false);
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'sensor':
        return '📡';
      case 'switch':
        return '💡';
      case 'ir_controller':
        return '🌡️';
      default:
        return '🔌';
    }
  };

  const getStatusColor = (online) => {
    return online ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800';
  };

  const isLocalConfig = device.productId === 'sensor_001' || device.productId === 'switch_001';

  return (
    <div
      onClick={() => onClick(device)}
      className="bg-white rounded-lg shadow-md hover:shadow-xl transition-all duration-300 cursor-pointer border-2 border-transparent hover:border-blue-500 overflow-hidden"
    >
      {/* Header */}
      <div className="p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className="text-4xl">{getTypeIcon(device.type)}</div>
            <div>
              <h3 className="text-lg font-bold text-gray-800">{device.name}</h3>
              <p className="text-sm text-gray-500">{device.productName || 'N/A'}</p>
            </div>
          </div>
          <div className="flex flex-col items-end space-y-1">
            <span
              className={`px-3 py-1 text-xs font-semibold rounded-full ${getStatusColor(
                device.online
              )}`}
            >
              {device.online ? 'En ligne' : 'Hors ligne'}
            </span>
            {isLocalConfig && (
              <span className="px-2 py-1 text-xs font-semibold rounded bg-blue-100 text-blue-800">
                Config locale
              </span>
            )}
          </div>
        </div>

        {/* Info */}
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-500">Type:</span>
            <span className="font-medium text-gray-800 capitalize">{device.type}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-500">Device ID:</span>
            <span className="font-mono text-xs text-gray-600">
              {device.deviceId.substring(0, 12)}...
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-500">Dernière sync:</span>
            <span className="text-gray-600">
              {new Date(device.lastSynced).toLocaleDateString('fr-FR')}
            </span>
          </div>
        </div>

        {/* Badge Associé */}
        <div className="mt-3 pt-3 border-t border-gray-200">
          {device.isAssociated ? (
            <div className="flex items-center justify-between">
              <span className="px-3 py-1 bg-purple-100 text-purple-700 text-xs font-semibold rounded-full flex items-center gap-1">
                🔗 Associé
              </span>
              {device.associationName && (
                <span className="text-xs text-gray-600 truncate max-w-[120px]" title={device.associationName}>
                  {device.associationName}
                </span>
              )}
            </div>
          ) : (
            <span className="px-3 py-1 bg-gray-100 text-gray-600 text-xs font-semibold rounded-full">
              ⭕ Non associé
            </span>
          )}
        </div>
      </div>

      {/* ✅ CONTRÔLE DIRECT POUR SWITCH */}
      {device.type === 'switch' && device.online && (
        <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-semibold text-gray-700">Contrôle direct:</span>
            {loading ? (
              <span className="text-xs text-gray-500">Chargement...</span>
            ) : (
              <span className={`text-xs font-bold ${deviceState ? 'text-green-600' : 'text-gray-500'}`}>
                {deviceState ? '⚡ ALLUMÉ' : '⭕ ÉTEINT'}
              </span>
            )}
          </div>
          
          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={(e) => handleDirectControl('on', e)}
              disabled={controlling || loading}
              className={`px-4 py-2.5 rounded-lg font-bold text-sm transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-105 ${
                deviceState
                  ? 'bg-green-600 text-white shadow-lg shadow-green-500/50'
                  : 'bg-green-500 text-white hover:bg-green-600 hover:shadow-lg'
              }`}
            >
              {controlling ? '⏳' : deviceState ? '✅ ON' : '🔆 ON'}
            </button>
            
            <button
              onClick={(e) => handleDirectControl('off', e)}
              disabled={controlling || loading}
              className={`px-4 py-2.5 rounded-lg font-bold text-sm transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-105 ${
                !deviceState
                  ? 'bg-gray-600 text-white shadow-lg shadow-gray-500/50'
                  : 'bg-red-500 text-white hover:bg-red-600 hover:shadow-lg'
              }`}
            >
              {controlling ? '⏳' : !deviceState ? '✅ OFF' : '🌙 OFF'}
            </button>
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="px-6 py-4 bg-white border-t border-gray-200">
        <button className="w-full text-blue-500 hover:text-blue-700 font-semibold text-sm flex items-center justify-center gap-2 hover:gap-3 transition-all">
          Voir les détails
          <span>→</span>
        </button>
      </div>
    </div>
  );
};

export default DeviceCard;